// Content access for races and blog posts.
// Sourced from the original WordPress database export (content-as-code), so the
// React app needs no CMS. Add new entries by editing the JSON files in
// src/content and dropping any images under public/content-images.
import racesData from '../content/races.json';
import blogData from '../content/blog.json';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const races = racesData;
export const blogPosts = blogData;

export const getRaceBySlug = (slug) => races.find((r) => r.slug === slug);
export const getPostBySlug = (slug) => blogPosts.find((p) => p.slug === slug);

/** Parse a race date stored as "YYYYMMDD". */
export function parseRaceDate(raw) {
  if (!raw) return null;
  const s = String(raw);
  const y = parseInt(s.slice(0, 4), 10);
  const m = parseInt(s.slice(4, 6), 10) - 1;
  const d = parseInt(s.slice(6, 8), 10);
  return new Date(y, m, d);
}

/** Today's date as "YYYYMMDD" for comparing against race dates. */
function todayYmd() {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}${m}${d}`;
}

/** Most recent race whose date is in the past (front page "Previous Race"). */
export function getPreviousRace() {
  const today = todayYmd();
  return (
    [...races]
      .filter((r) => String(r.date) < today)
      .sort((a, b) => String(b.date).localeCompare(String(a.date)))[0] || null
  );
}

/** Soonest race whose date is today or later (front page "Upcoming Race"). */
export function getNextRace() {
  const today = todayYmd();
  return (
    [...races]
      .filter((r) => String(r.date) >= today)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))[0] || null
  );
}

/** Format a race date as "July 23, 2022" (PHP "F j, Y"). */
export function formatRaceDateLong(raw) {
  const d = parseRaceDate(raw);
  if (!d) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Three-line race date parts for the archive cards (M / dd / Y). */
export function raceDateParts(raw) {
  const d = parseRaceDate(raw);
  if (!d) return { month: '', day: '', year: '' };
  return {
    month: MONTHS[d.getMonth()].slice(0, 3),
    day: String(d.getDate()).padStart(2, '0'),
    year: String(d.getFullYear()),
  };
}

/** Format a blog post date ("2022-07-19 06:03:40") as "Jul 19, 2022" (PHP "M d, Y"). */
export function formatPostDate(raw) {
  if (!raw) return '';
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const month = MONTHS[d.getMonth()].slice(0, 3);
  const day = String(d.getDate()).padStart(2, '0');
  return `${month} ${day}, ${d.getFullYear()}`;
}

/**
 * Build a WordPress-style excerpt: strip HTML/blocks, take ~55 words.
 * Used where the_excerpt() appeared and the stored excerpt is empty.
 */
export function excerptFor(post, words = 55) {
  if (post.excerpt && post.excerpt.trim()) return post.excerpt;
  const text = (post.content || '')
    .replace(/<!--[\s\S]*?-->/g, ' ') // strip Gutenberg block comments
    .replace(/<[^>]+>/g, ' ') // strip HTML tags
    .replace(/\s+/g, ' ')
    .trim();
  const split = text.split(' ');
  if (split.length <= words) return text;
  return split.slice(0, words).join(' ') + '…';
}
